import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';
import { Header } from '../../components';
import { useStateContext } from '../../contexts/ContextProvider';
import useKYCStatusRedirect from '../../hooks/useKYCStatusRedirect';
import { userRequest } from '../../utils/requestMethods';
import { formatCurrency } from '../../utils/currencyUtils';
import SyncBalanceConfirmModal from '../../components/Finance/SyncBalanceConfirmModal';

const Reconciliation = () => {
  useKYCStatusRedirect('/finance/reconciliation');
  const { user, school } = useStateContext();
  const schoolId = school?._id;
  const isOwner = user?.role === 0;

  const [rows, setRows] = useState([]);
  const [fundNames, setFundNames] = useState({});
  const [loading, setLoading] = useState(false);
  const [syncingFundId, setSyncingFundId] = useState(null);

  const fetchData = async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const [fundsRes, reconRes] = await Promise.all([
        userRequest.get(`/schools/${schoolId}/funds?limit=100`),
        userRequest.get(`/schools/${schoolId}/funds/reconciliation`),
      ]);
      const names = {};
      const funds = fundsRes.data?.data?.funds || fundsRes.data?.funds || [];
      funds.forEach(f => { names[f._id] = f.name; });
      setFundNames(names);
      const list = reconRes.data?.funds || reconRes.data?.items || (Array.isArray(reconRes.data) ? reconRes.data : []);
      setRows(list);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      <Header
        category="Finance"
        title="Reconciliation"
        description="Match bank transactions against recorded student fee payments and expenses for accurate accounting."
      />

      <div className="border border-gray-200 rounded-2xl overflow-hidden mt-6">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">Fund</th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">Cached Balance</th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">Ledger Balance</th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">Drift</th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">Status</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-gray-400">Loading reconciliation status...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-gray-400">No funds to reconcile.</td></tr>
            ) : rows.map((row) => {
              const id = row.fundId || row.fund_id;
              return (
                <tr key={id}>
                  <td className="px-5 py-3 text-sm font-medium text-gray-800">{fundNames[id] || row.name || id}</td>
                  <td className="px-5 py-3 text-sm text-gray-600">{formatCurrency(row.cachedBalance)}</td>
                  <td className="px-5 py-3 text-sm text-gray-600">{formatCurrency(row.ledgerBalance)}</td>
                  <td className="px-5 py-3 text-sm text-gray-600">{formatCurrency(row.drift)}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                      row.inSync ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {row.inSync ? <FiCheckCircle /> : <FiAlertTriangle />}
                      {row.inSync ? 'In Sync' : 'Drift Detected'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {!row.inSync && (
                      <button
                        type="button"
                        disabled={!isOwner}
                        title={!isOwner ? 'Only the school owner can sync balance' : ''}
                        onClick={() => setSyncingFundId(id)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full text-white bg-yellow-600 hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FiRefreshCw className="w-3.5 h-3.5" />
                        Fix Balance
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {syncingFundId && (
        <SyncBalanceConfirmModal
          isOpen={!!syncingFundId}
          onClose={() => setSyncingFundId(null)}
          schoolId={schoolId}
          fundId={syncingFundId}
          onSynced={fetchData}
        />
      )}
    </div>
  );
};

export default Reconciliation;
